/**
 * 「超时」作业项的**共享判据**（家长端与学生端各有一处渲染，必须同一套口径）。
 *
 * ## 为什么要有这个文件
 *
 * 用户最早的需求是：「没有完成的作业任务，会一直显示，在「超时」标签中显示，
 * 直到完成或家长点击放弃」。这句话在产品上对应两件**必须一致**的事：
 *
 *   1. 一条作业项「算不算超时、超时多久」的**读法**；
 *   2. 「已超时 3 小时」这句**人话的写法**。
 *
 * 家长端（`ParentHomeworkPage`）与学生端（`StudentHomeworkPage`）都要显示它。
 * 这个项目已经因为"同一个概念在多处各实现一份"反复踩坑（见 `homework-draft.ts`
 * 与 `api/homework.ts` 里 `describeSubjectSchedule` / `dueTimeInputOf` 的注释），
 * 所以判据与文案都在这里实现**唯一一份**，两边只负责排版。
 *
 * ## 两条硬约定
 *
 * 1. **超时秒数只认服务端**。服务端在 `checkin` 与超时队列两条路径上都算好了
 *    `overdueSeconds`（按家庭时区，见 `checkins.service.ts`），前端
 *    **绝不用本地 `Date.now()` 减 `dueAt` 重算** —— 那等于把时区口径变成两份，
 *    而且是"改手机时间就能改结论"的一份。因此这里的入参只有秒数，没有时间戳。
 *
 * 2. **`overdueSeconds` 本身就携带了"是否超时"**：判据是
 *    `due_at < now()` 且状态未终结，所以秒数 > 0 ⟺ 已超时。
 *    后端另外给的 `overdue: boolean` 是同一个判据的布尔面，两者不会打架。
 *
 * ## 为什么超时与「状态」是两件事
 *
 * 「超时」是**派生视图，不是状态**：`pending / running / paused / auto_paused /
 * submitted` 的作业项过了 `due_at` 都算超时，而它们**仍然可以做**
 * （孩子做完点完成 → 家长验收通过，它才退出超时队列）。
 * 所以 `overdueSeconds > 0` 成立时，界面要写"已超时 X"，而**不是**"已失败"。
 *
 * 这一点还牵出一个刻意的产品行为，⛔ **不要"修"它**：
 * 家长没填截止时刻时，服务端把科目的 `due_time` 落成 23:59
 * （`HOMEWORK_DEFAULT_DUE_TIME`，语义是"当天完成"），于是"当天截止"的作业项
 * 要等到**第二天**才进超时队列。这正是"某天还没过完就不该出现在超时里"，
 * 也是 `dayEnd` 那个默认值存在的理由（若留空会退化成当天 0 点起就假超时）。
 * 相关的另一份判据在同目录的 `constants.ts`（常量）与 `time.ts`（时区换算）。
 */
import type { CheckinStatus } from './constants.js';

/**
 * 一条作业项超时多久的**展示文案**：`'25 分钟'` / `'3 小时 5 分'`。
 *
 * ⚠️ 与 `duration.ts` 的 `formatDuration()`（`'19 分 23 秒'`，实际用时，**永不丢秒**）
 * 是**两个用途**，不要合并：
 *   - `formatDuration` 给"**实际用时 / 累计投入**"用（`actualSeconds`）——
 *     用户的真实投入必须看得见秒，见那个文件的模块头注释；
 *   - 本函数给"**还欠多久**"用（`overdueSeconds`，徽章与状态行），写法与家长端
 *     `ItemStatusLine` 里的「超时 3 小时 5 分」、学生端超时行里的「已超时 3 小时 5 分」
 *     **逐字一致**（带空格、四舍五入到分钟）。改这里 = 同时改两端，不会再出现
 *     "家长端写 3 小时 5 分、学生端写 185 分钟"。
 *
 * 秒数 ≤ 0（或非有限值）返回 `'—'`：它表示"没有可说的超时时长"，
 * 而不是"0 分钟"。调用方应先判 `homeworkOverdueSeconds()` 再决定要不要显示，
 * 所以这个占位符只在"硬要显示"的场合兜底。
 */
export function formatOverdueDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return '—';
  const minutes = Math.round(seconds / 60);
  // < 1 分钟但 > 0 秒：说成"不到 1 分钟"比"0 分钟"诚实（真的可以只超几秒）
  if (minutes < 1) return '不到 1 分钟';
  if (minutes < 60) return `${minutes} 分钟`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} 小时` : `${hours} 小时 ${rest} 分`;
}

/**
 * 一条作业项**要不要显示「已超时 X」**：要显示就返回秒数，否则 `null`。
 *
 * 判据 = 服务端给的 `overdueSeconds > 0` **且**状态未终结。
 *
 * 服务端 `UNFINISHED_STATUSES`（`apps/api/src/modules/checkins/checkins.rules.ts`）
 * 里能进超时队列的只有 `pending / running / paused / auto_paused / submitted` 五个；
 * 这里额外排掉 `done / skipped / missed`，是因为**这一页可能已经打开着**：
 * `overdueSeconds` 是服务端**按请求时刻**算好的静态值，而用户完全可能在看到它之后
 * 点了「完成」或「放弃」——那一瞬间缓存里的 `status` 已经不是 `pending` 了，
 * 但 `overdueSeconds` 还是旧的。若不加这道判据，界面上会出现
 * "已完成 ✅ / 已放弃 🚫 + 已超时 3 小时"这种自相矛盾的一行
 * （"已放弃"那一项更荒谬：它已经终结了，不再欠任何东西）。
 *
 * 与 `describeSubjectSchedule` / `dueTimeInputOf` 同一套思路：**读与写、
 * 家长端与学生端共用同一个出口**，而不是各自 `> 0` 一下。
 */
export function homeworkOverdueSeconds(
  checkin: { status: CheckinStatus; overdueSeconds: number } | null | undefined,
): number | null {
  if (!checkin) return null;
  if (checkin.status === 'done' || checkin.status === 'skipped' || checkin.status === 'missed') {
    return null;
  }
  return checkin.overdueSeconds > 0 ? checkin.overdueSeconds : null;
}

/**
 * 一条作业项**还能不能做**：`pending / running / paused / auto_paused` 可以。
 *
 * `submitted`（待家长验收）与三个终态都**不能**：前者在等家长点通过/打回，
 * 后者已经是结论。超时行里的"仍然可以做"说明与操作按钮都按它决定 ——
 * 两端唯一的差异是家长端多一个「放弃」（见 `canSkipHomeworkCheckin`，
 * 后端也只允许家长 owner/editor 调用）。
 */
export function isHomeworkActionable(
  status: CheckinStatus | string | null | undefined,
): boolean {
  return (
    status === 'pending' || status === 'running' || status === 'paused' || status === 'auto_paused'
  );
}

/**
 * 「放弃」确认弹窗里的那句**人话后果**（家长端与学生端的说法必须可对照）。
 *
 * 五个事实缺一不可，少说一条都会让家长点下去之后才发现：
 *   1. 这一项**不再出现在孩子的作业里**（`skipped` = 终结，孩子端显示"不用再做了"）；
 *   2. 它**不算完成**（`done` 才是完成；科目完成判定要求至少 1 项 `done`）；
 *   3. **已经产生的记录会保留**（打卡记录与积分依据不删，见契约的 `retained` 语义）；
 *   4. 它的**打卡记录会立刻退出超时队列**（这正是家长点这一下想要的结果）；
 *   5. **可以撤回**（2026-10-17 新增：`POST /checkins/:id/restore` 把 `skipped` 退回
 *      `pending`）。第 5 条是给"点错了"留的台阶 —— 用户就是在这条上踩过坑
 *      （孩子误点放弃之后没有任何回程）。
 *
 * ⚠️ 用"孩子"与"这一项"而不是"checkin / habit"：这段文案是**给人看的**，
 * 不是术语表。术语（`skipped`）留给代码。
 */
export const HOMEWORK_SKIP_CONSEQUENCE_TEXT =
  '放弃后这一项不再出现在孩子的作业里，也不算完成；已经产生的记录会保留。' +
  '放弃完它会立刻从「超时」里消失。' +
  '点错了可以撤回：今天之内在那一项上点「恢复成未完成」就能退回未完成。';

/**
 * 「放弃」成功之后的一句话结果（两端共用，措辞一致）。
 *
 * `reason` 会写进 `checkin.skip_reason` 且**孩子能看到**（孩子端显示
 * "家长已放弃这一项，不用再做了 · <原因>"），所以填了原因时要**明说**这件事，
 * 否则家长会以为那是只给自己看的备忘。
 */
export function describeSkipResult(itemName: string, reason: string): string {
  const name = itemName.trim() || '这一项';
  const trimmed = reason.trim();
  return trimmed
    ? `已放弃「${name}」，孩子会看到你写的原因`
    : `已放弃「${name}」，孩子端不会再要求做这一项`;
}

// ================================================================ 验收（通过 / 打回）

/**
 * 一条待验收的作业项，**渲染验收动作**所需要的全部字段。
 *
 * ## 为什么这些判定必须住在 shared
 *
 * 2026-10-15 用户实测报的 bug：
 *
 * > 昨天孩子提交了作业，但我没有检查，就没有点通过，它就过期了，过期是没问题。
 * > 问题在于，我现在检查了作业，我想点通过，**却没有按钮了**。
 *
 * 根因是"验收动作只做在「今天的作业」标签里"，而**「超时」是跨天的派生视图**
 * （见 `apps/web/src/components/homework/OverdueRow.tsx` 与
 * `ParentHomeworkPage` 的 `OverdueSection`）—— 昨天提交、今天才变成超时的项
 * **只会出现在超时列表里**，于是家长在唯一看得见它的地方**没有任何验收入口**。
 *
 * 修法是"让两个视图复用同一套动作"，而不是"在超时列表里再写一份"：
 * 本项目已经因为同一个规则两处实现反复踩坑（见 `homework-draft.ts` 的模块头）。
 * 所以：
 *   - **要不要渲染按钮** = 这里的 `homeworkApprovalActions()`；
 *   - **按钮旁边那句人话** = 这里的 `homeworkApprovalPrompt()`（分段，哪一段加粗也在这里）；
 *   - **点下去之后说什么** = 这里的 `describeHomeworkApprovalResult()` /
 *     `describeHomeworkRejectResult()`；
 *   - 真正的写操作与缓存失效 = `apps/web/src/lib/homeworkApproval.tsx`
 *     （家长端页面与超时区块共用的**同一个** hook）。
 *
 * ## 权限只有服务端一份
 *
 * `canApprove` 是**服务端**算好的（`canApproveCheckin(actor)`，家长 owner/editor），
 * 前端**不自己判角色**。所以学生端、只读家长拿到的都是 `false`，
 * 两个视图都因此不会渲染"点了会失败"的按钮。
 */
export interface HomeworkApprovalTarget {
  status: CheckinStatus | string;
  /** 服务端给的 `canApproveCheckin(actor)`。 */
  canApprove: boolean;
  /**
   * 这一项的分值；`null` = **服务端没给**（拿不到就显示「—」，绝不吞成 0）。
   *
   * ⚠️ `0` 与 `null` 是**两件不同的事实**，界面文案因此分成三支：
   *   - `> 0`：通过就加这么多分；
   *   - `0`：这一项**明确不计分**（`HOMEWORK_DEFAULT_POINTS_PER_UNIT` 就是 0），
   *     通过后余额一动不动，必须直说，否则家长会以为"验收了怎么没加分"是坏了；
   *   - `null`：**数据缺失**，什么都不断言（不写"通过才计分"，也不写"不计分"）。
   */
  pointsPerUnit: number | null;
}

/** 验收面板上那两个按钮的显隐判据（两个视图**共用**这一条）。 */
export function homeworkApprovalActions(target: HomeworkApprovalTarget): {
  /** true = 渲染「通过 / 打回」。 */
  showApproveActions: boolean;
  /** true = 面板上必须写"这一项不计分"（分值为 0 的既有口径，见接口契约）。 */
  pointsAreZero: boolean;
  /** true = 不知道分值，只能给中性文案。 */
  pointsUnknown: boolean;
} {
  const pointsUnknown = target.pointsPerUnit === null;
  return {
    // 判据刻意只有这两条：状态与服务端权限。⛔ 不要在这里加"哪一个视图"
    // 之类的条件 —— 那正是"昨天提交的项在超时列表里看不到按钮"的来源。
    showApproveActions: target.status === 'submitted' && target.canApprove,
    pointsAreZero: !pointsUnknown && (target.pointsPerUnit as number) <= 0,
    pointsUnknown,
  };
}

/** 分值 > 0 时，面板与 Toast 里那个「+N 分」的写法（唯一一份）。 */
export function describeHomeworkPoints(pointsPerUnit: number): string {
  return `+${pointsPerUnit} 分`;
}

/**
 * 验收面板旁边那句**人话**该用哪一支 —— 返回**变体名**，不返回带 `**` 的字符串。
 *
 * ⚠️ 为什么是变体名而不是整句字符串：这一段里有两处必须加粗的短语
 * （「分值是 0（不计分）」「不会加分」），而 JSX 的加粗只能用 `<strong>` 表达。
 * 如果这里返回一个带 Markdown `**` 的字符串，界面要么原样渲染出星号，
 * 要么在每个调用点各写一遍"按 `**` 切分"——那正是"同一个规则两处实现"。
 * 所以**判据在这里**（唯一一份），加粗这类排版留给组件。
 *
 * 三支对应三件**不同**的事实（见 `HomeworkApprovalTarget.pointsPerUnit`）：
 * 分值为 0 时说"通过才计分"是**假话**；分值缺失时又什么都不能断言。
 */
export type HomeworkApprovalPromptVariant = 'points-unknown' | 'points-zero' | 'points';

/**
 * 验收面板那句人话的**分段结构**：`strong: true` 的段落由组件渲染成 `<strong>`。
 *
 * ⚠️ 为什么是分段而不是一整句字符串：这句话里有两处必须加粗的短语，
 * 而加粗只能用 JSX 的 `<strong>` 表达。让这里返回带 Markdown `**` 的字符串
 * 会逼着每个调用点各写一遍"按 `**` 切分" —— 那正是"同一个规则两处实现"。
 * 所以**文案在这里**（唯一一份），"哪一段加粗"也在这里，
 * 组件只做"`strong ? <strong> : 原文`"这一件事。
 */
export type HomeworkApprovalPromptSegment = { text: string; strong: boolean };

export interface HomeworkApprovalPrompt {
  variant: HomeworkApprovalPromptVariant;
  segments: HomeworkApprovalPromptSegment[];
}

/**
 * 验收面板旁边那句**人话**：三支变体对应三件**不同**的事实
 * （见 `HomeworkApprovalTarget.pointsPerUnit`）——
 * 分值为 0 时说"通过才计分"是**假话**；分值缺失时又什么都不能断言。
 */
export function homeworkApprovalPrompt(target: HomeworkApprovalTarget): HomeworkApprovalPrompt {
  const points = target.pointsPerUnit;
  const { pointsAreZero, pointsUnknown } = homeworkApprovalActions(target);
  if (pointsUnknown || points === null) {
    return {
      variant: 'points-unknown',
      segments: [
        { text: '📤 孩子在等你验收：通过这一项就算完成，打回则退回未完成、让他重做。', strong: false },
      ],
    };
  }
  if (pointsAreZero) {
    return {
      variant: 'points-zero',
      segments: [
        { text: '📤 孩子在等你验收。这一项的', strong: false },
        { text: '分值是 0（不计分）', strong: true },
        { text: '，通过后', strong: false },
        { text: '不会加分', strong: true },
        { text: '；想计分请先点「修改」在这一项里填分值。', strong: false },
      ],
    };
  }
  return {
    variant: 'points',
    segments: [
      {
        text: `📤 孩子在等你验收：通过才计分（${describeHomeworkPoints(points)}），打回则退回未完成。`,
        strong: false,
      },
    ],
  };
}

/** 上面那段文案的**纯文本**（去掉加粗标记）—— 供测试与 Toast 之类只用文字的场合。 */
export function homeworkApprovalPromptText(target: HomeworkApprovalTarget): string {
  return homeworkApprovalPrompt(target)
    .segments.map((segment) => segment.text)
    .join('');
}

/**
 * 「通过」成功后的 Toast。
 *
 * 分值为 0 时**必须**直说"没有加分"（服务端不会写 delta = 0 的流水，
 * `applyPointsEntry` 拒绝 0 的变动），否则家长会以为"孩子的分怎么没变"是系统坏了。
 * 分值缺失（`null`）时既不报分数也不加戏。
 */
export function describeHomeworkApprovalResult(
  itemName: string,
  pointsPerUnit: number | null,
  pointsAwarded: number,
): string {
  const name = itemName.trim() || '这一项';
  if (pointsPerUnit === null) return `已通过「${name}」`;
  if (pointsPerUnit <= 0) {
    return (
      `已通过「${name}」—— 这一项的分值是 0（不计分），所以没有加分。` +
      '想计分就点「修改」，在这一项的分值里填一个数（留空 = 不计分）。'
    );
  }
  return pointsAwarded > 0
    ? `已通过「${name}」，加了 ${pointsAwarded} 分`
    : `已通过「${name}」`;
}

/**
 * 「打回」成功后的 Toast（填了原因要说明孩子看得到 —— 与「放弃」同一套口径）。
 */
export function describeHomeworkRejectResult(itemName: string, reason: string): string {
  const name = itemName.trim() || '这一项';
  return reason.trim()
    ? `已打回「${name}」，孩子会看到你写的原因`
    : `已打回「${name}」，孩子要重做`;
}
